import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { rememberReturnTo, resolveReturnTo, loginHref } from "@/lib/authReturn";
import { Loader2 } from "lucide-react";
import AuthShell from "@/pages/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";

const ROLES = [
  { value: "songwriter", label: "Songwriter" },
  { value: "music_industry", label: "Music Industry" },
  { value: "vendor", label: "Vendor" },
  { value: "service_provider", label: "Service Provider" },
  { value: "public_listener", label: "Public Listener" },
];

export default function Register() {
  const { register, error, startGoogleLogin, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = resolveReturnTo(location);
  useEffect(() => { rememberReturnTo(dest); }, [dest]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "songwriter",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      role: form.role,
    });
    setBusy(false);
    if (ok) navigate(dest, { replace: true });
  };

  if (!loading && isAuthenticated) return <Navigate to={dest} replace />;

  return (
    <AuthShell>
      <div className="max-w-md w-full mx-auto">
        <div className="font-display uppercase text-[11px] tracking-[0.4em] text-gold mb-3">
          Join Songwriter Square
        </div>
        <h1 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-white">
          Claim your <span className="text-gold-sheen">Square</span>
        </h1>
        <p className="mt-3 text-white/60 text-sm">
          Ownership stays yours. Discovery works on merit. Welcome to the
          ecosystem.
        </p>

        <form
          onSubmit={submit}
          data-testid={TID.auth.registerForm}
          className="mt-10 space-y-5"
          noValidate
        >
          <div className="space-y-2">
            <Label className="font-display uppercase text-[11px] tracking-[0.35em] text-white/70">
              Full Name
            </Label>
            <Input
              value={form.name}
              onChange={setField("name")}
              data-testid={TID.auth.registerName}
              required
              placeholder="Your name"
              className="rounded-none bg-matte-card border-white/10 focus-visible:border-gold focus-visible:ring-gold/40 h-11 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-display uppercase text-[11px] tracking-[0.35em] text-white/70">
              Email
            </Label>
            <Input
              type="email"
              value={form.email}
              onChange={setField("email")}
              data-testid={TID.auth.registerEmail}
              required
              placeholder="you@studio.com"
              className="rounded-none bg-matte-card border-white/10 focus-visible:border-gold focus-visible:ring-gold/40 h-11 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-display uppercase text-[11px] tracking-[0.35em] text-white/70">
              Password
            </Label>
            <Input
              type="password"
              value={form.password}
              onChange={setField("password")}
              data-testid={TID.auth.registerPassword}
              required
              minLength={8}
              placeholder="At least 8 characters"
              className="rounded-none bg-matte-card border-white/10 focus-visible:border-gold focus-visible:ring-gold/40 h-11 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-display uppercase text-[11px] tracking-[0.35em] text-white/70">
              I am a
            </Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
            >
              <SelectTrigger
                data-testid={TID.auth.registerRole}
                className="rounded-none bg-matte-card border-white/10 focus:border-gold focus:ring-gold/40 h-11 text-white"
              >
                <SelectValue placeholder="Choose your role" />
              </SelectTrigger>
              <SelectContent className="bg-matte-card border-white/10 text-white rounded-none">
                {ROLES.map((r) => (
                  <SelectItem
                    key={r.value}
                    value={r.value}
                    className="focus:bg-white/[0.04] focus:text-gold"
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div
              data-testid={TID.auth.registerError}
              className="border border-red-500/40 bg-red-500/10 text-red-200 px-4 py-3 text-sm"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid={TID.auth.registerSubmit}
            className="w-full rounded-none h-11 bg-goldSheen text-black font-display uppercase tracking-widest hover:brightness-110 shadow-goldSoft hover:shadow-goldGlow border border-gold/60 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
          </Button>
        </form>

        <div className="my-8 flex items-center gap-4">
          <div className="h-px flex-1 bg-white/10" />
          <span className="font-display uppercase text-[10px] tracking-[0.4em] text-white/40">
            or
          </span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <Button
          type="button"
          onClick={startGoogleLogin}
          data-testid={TID.auth.registerGoogle}
          variant="outline"
          className="w-full rounded-none h-11 bg-transparent border-white/15 hover:border-gold hover:bg-white/[0.03] text-white font-display uppercase tracking-widest"
        >
          Continue with Google
        </Button>

        <div className="mt-8 text-sm text-white/60">
          Already a member?{" "}
          <Link
            to={loginHref(dest === "/app/dashboard" ? null : dest)}
            data-testid={TID.auth.toLogin}
            className="text-gold hover:text-gold-bright underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
